import axios, { AxiosInstance, AxiosResponse, AxiosRequestConfig } from 'axios';
import { IApiResource } from './IApiResource';
import { JsonConvert } from 'json2typescript';

export class ApiService {
    private static API_ZG_URI: string | undefined = process.env.VUE_APP_API_ZG_URI;
    protected httpClient: AxiosInstance;
    private jsonConvert: JsonConvert;
    private axiosConfig: AxiosRequestConfig = {};

    constructor(version: string) {
        this.httpClient = this.CreateHttpClient(version);
        this.jsonConvert = new JsonConvert();
    }

    public async Get<TResource extends object>(type: (new () => TResource), resource: string): Promise<TResource> {
        const response = await this.httpClient.get(resource + '/');
        return this.jsonConvert.deserialize(response.data, type) as TResource;
    }

    public async GetArray<TResource extends object>(
        type: (new () => TResource),
        resource: string,
        params: {} = {}): Promise<TResource[]> {

        const queryParams = this.buildQueryParams(params);
        const response = await this.httpClient.get(resource + '/' + queryParams);
        return this.jsonConvert.deserializeArray(response.data, type) as TResource[];
    }

    public buildQueryParams(params: {}): string {
        let result = '';
        for (const [key, value] of Object.entries(params)) {
            if (result === '') {
                result = '?';
            } else {
                result += '&';
            }
            result += key + '=' + value;
        }

        return result;
    }

    public async PostResource<TResource extends object>(
        type: (new () => TResource),
        resource: string,
        payload: TResource,
    ): Promise<AxiosResponse> {
        const serializedPayload = this.jsonConvert.serializeObject(payload);
        const response = await this.httpClient.post(resource, serializedPayload);
        return response;
    }

    public Post(resource: string): void {
        this.httpClient.post(resource + '/', '', {headers: { 'Content-type': 'text/plain' }});
    }

    public async PostPayLoad<TResource extends IApiResource>(
        type: (new () => TResource),
        resource: string,
        payload: TResource,
    ): Promise<TResource> {
        const serializedPayload = this.jsonConvert.serializeObject(payload);
        const response = await this.httpClient.post(resource + '/', serializedPayload);
        return this.jsonConvert.deserializeObject(response.data, type);
    }

    public async Delete<TResource extends IApiResource>(resource: string, obj: TResource): Promise<any> {
        const url = resource + '/' + obj.id;
        return this.httpClient.delete(url + '/');
    }

    private InitHeaders(): void {
        const token = localStorage.getItem('token');
        this.axiosConfig.headers = {
            'Authorization': 'Token ' + token,
            'Content-Type': 'multipart/form-data',
        };
        if (this.IsInternetExplorer()) {
            this.axiosConfig.headers.pragma = 'no-cache';
        }
    }

    private InitConfig(version: string): void {
        const endpointUrl = this.GetServiceUrl(version);
        this.InitHeaders();
        this.axiosConfig.timeout = 60000;
        this.axiosConfig.baseURL = endpointUrl;
    }

    private CreateHttpClient(version: string): AxiosInstance {
        this.InitConfig(version);
        return axios.create(this.axiosConfig);
    }

    private GetServiceUrl(version: string): string {
        return ApiService.API_ZG_URI + '/api/' + version + '/';
    }

    private IsInternetExplorer(): boolean {
        return navigator.userAgent.indexOf('MSIE') !== -1 || navigator.appVersion.indexOf('Trident/') > -1;
    }
}
